import type { CSSProperties } from 'react'

/* Turning what somebody picked in the console into something a browser draws.
 *
 * Two mechanisms, and the difference between them is the whole trick.
 *
 * Colours are set as *theme variables* on the wrapper — `--color-lime`,
 * `--color-platinum`, `--color-steel`. Tailwind v4 compiles `text-lime` to
 * `color: var(--color-lime)`, so redefining that variable on one section
 * recolours every accent inside it — the buttons, the rules beside the
 * eyebrows, the gradient in the headline, the icon rings — without a single
 * one of those components knowing the CMS exists. Colour inherits, so this
 * costs one inline style and no CSS at all.
 *
 * Everything else — padding, widths, sizes — does not inherit, so it needs a
 * rule that reaches the right descendant. Those live in index.css, each one
 * guarded by a `data-cms-*` flag that is only present when somebody has
 * actually set that control. A section nobody has restyled matches none of
 * them and renders exactly as it was designed.
 */

export type StyleMap = Record<string, string>

const set = (value: string | undefined) => Boolean(value && value.trim())

/** The inline custom properties for a section, and the flags that switch on
    its rules in index.css. */
export function sectionStyle(style: StyleMap | undefined) {
  const s = style ?? {}
  const vars: Record<string, string> = {}

  /* The accent, in all three of its readings. A headline's gradient runs
     from `lime` to `lime-lift`; leaving the second one behind would tint half
     of every gradient on the page and none of the other half. */
  if (set(s.accent)) {
    vars['--color-lime'] = s.accent
    vars['--color-lime-lift'] = s.accent
    vars['--color-lime-deep'] = s.accent

    /* The primary button is a gradient rather than a fill, so it needs its
       four stops restating or it stays lime on a section that is no longer
       lime. Lit from above, the way it always is — the top stop is the accent
       with a little white in it. */
    vars['--btn-lime-top'] = `color-mix(in srgb, ${s.accent} 86%, #ffffff)`
    vars['--btn-lime-bottom'] = s.accent
    vars['--btn-lime-top-hover'] = `color-mix(in srgb, ${s.accent} 72%, #ffffff)`
    vars['--btn-lime-bottom-hover'] = `color-mix(in srgb, ${s.accent} 92%, #ffffff)`
  }
  if (set(s.headingColour)) vars['--color-platinum'] = s.headingColour
  if (set(s.bodyColour)) {
    vars['--color-steel'] = s.bodyColour
    vars['--color-silver'] = s.bodyColour
  }

  if (set(s.background)) vars['--cms-bg'] = s.background
  if (set(s.paddingTop)) vars['--cms-pt'] = s.paddingTop
  if (set(s.paddingBottom)) vars['--cms-pb'] = s.paddingBottom
  if (set(s.maxWidth)) vars['--cms-mw'] = s.maxWidth
  if (set(s.headingSize)) vars['--cms-hsize'] = s.headingSize
  if (set(s.headingWeight)) vars['--cms-hweight'] = s.headingWeight
  if (set(s.headingTracking)) vars['--cms-htrack'] = s.headingTracking
  if (set(s.bodySize)) vars['--cms-bsize'] = s.bodySize
  if (set(s.align)) vars['--cms-align'] = s.align

  /* The corner radius rides the same mechanism as the colours: `rounded-xl`
     compiles to `border-radius: var(--radius-xl)`, so setting the three sizes
     this site actually uses reshapes every card in the band without a rule of
     our own. */
  if (set(s.radius)) {
    vars['--radius-lg'] = s.radius
    vars['--radius-xl'] = s.radius
    vars['--radius-2xl'] = s.radius
  }

  /* Present-or-absent attributes rather than values: a CSS rule can then ask
     "was this set?" without having to compare against a sentinel, and an
     unset control leaves no trace in the DOM at all. */
  const flags: Record<string, string> = {}
  if (set(s.background)) flags['data-cms-bg'] = ''
  if (set(s.paddingTop)) flags['data-cms-pt'] = ''
  if (set(s.paddingBottom)) flags['data-cms-pb'] = ''
  if (set(s.maxWidth)) flags['data-cms-mw'] = ''
  if (set(s.headingSize)) flags['data-cms-hsize'] = ''
  if (set(s.headingWeight)) flags['data-cms-hweight'] = ''
  if (set(s.headingTracking)) flags['data-cms-htrack'] = ''
  if (set(s.bodySize)) flags['data-cms-bsize'] = ''
  if (set(s.align)) flags['data-cms-align'] = ''

  return { style: vars as CSSProperties, flags }
}

/** The same, for one element inside a section. A much smaller vocabulary:
    an element is a card or a line, not a band. */
export function elementStyle(style: StyleMap | undefined) {
  const s = style ?? {}
  const vars: Record<string, string> = {}

  if (set(s.colour)) {
    vars['--color-platinum'] = s.colour
    vars['--color-steel'] = s.colour
    vars['--color-silver'] = s.colour
  }
  if (set(s.accent)) {
    vars['--color-lime'] = s.accent
    vars['--color-lime-lift'] = s.accent
    vars['--color-lime-deep'] = s.accent
  }
  if (set(s.background)) vars['--cms-bg'] = s.background
  if (set(s.size)) vars['--cms-bsize'] = s.size
  if (set(s.weight)) vars['--cms-hweight'] = s.weight
  if (set(s.align)) vars['--cms-align'] = s.align

  const flags: Record<string, string> = {}
  if (set(s.background)) flags['data-cms-bg'] = ''
  if (set(s.size)) flags['data-cms-bsize'] = ''
  if (set(s.weight)) flags['data-cms-hweight'] = ''
  if (set(s.align)) flags['data-cms-align'] = ''

  return { style: vars as CSSProperties, flags }
}
